// Auto Appy AI navigation, in the same shape as the main app's workspaceNav.js.
// Every destination is an existing page; old URLs keep working.

const starts = (...prefixes) => (pathname) => prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
const botEditors = starts('/bot-library', '/bots', '/static-flow', '/static-flows', '/sales-bots', '/memory-bots');
const sourceEditors = starts('/faq-bots', '/knowledge-bots', '/product-bots', '/intake-bots');

const tab = (search = '') => new URLSearchParams(search).get('tab');

// AUTO-AI-17: one place to build (Studio), a ChatGPT-style Chat, and Developers for the API.
// `scoped` destinations carry ?workspace=<id> so the chosen organisation follows you.
export const NAV_GROUPS = [
  {
    id: 'daily',
    items: [
      { id: 'overview', label: 'Home', to: '/dashboard', scoped: true, icon: 'overview', match: starts('/dashboard') },
      { id: 'chat', label: 'Chat', to: '/chat', scoped: true, icon: 'chat', match: starts('/chat') },
      // AUTO-AI-18: text to speech, conversations, transcripts and cloned voices.
      { id: 'voice', label: 'Voice', to: '/voice', scoped: true, icon: 'voice', match: starts('/voice') },
      { id: 'activity', label: 'Conversations', to: '/activity', scoped: true, icon: 'activity', match: starts('/activity') },
    ],
  },
  {
    id: 'build',
    label: 'Build',
    items: [
      {
        // Assistants, customer bots, tools and knowledge live together in Studio.
        id: 'studio', label: 'Studio', to: '/bot-library', scoped: true, icon: 'studio',
        match: (pathname, search) => botEditors(pathname) || sourceEditors(pathname) || starts('/tools', '/ai-jobs', '/assistants', '/studio')(pathname)
          || (pathname === '/automation' && tab(search) === 'bots'),
      },
    ],
  },
  {
    id: 'connect',
    label: 'Connect',
    items: [
      {
        id: 'connections', label: 'Channels', to: '/connections', scoped: true, icon: 'connections',
        match: (pathname, search) => starts('/connections')(pathname) || (pathname === '/automation' && tab(search) !== 'bots'),
      },
      { id: 'developers', label: 'Developers', to: '/developers', scoped: true, icon: 'developers', match: starts('/developers', '/ai-api-keys') },
    ],
  },
];

export const FOOTER_ITEMS = [
  { id: 'usage', label: 'Usage', to: '/usage', scoped: true, icon: 'usage', match: starts('/usage') },
  { id: 'settings', label: 'Settings', to: '/settings', scoped: true, icon: 'settings', match: starts('/settings', '/contact-fields') },
  { id: 'guide', label: 'Guide', to: '/guide', icon: 'guide', match: starts('/guide') },
];

export function destination(item, workspaceId) {
  const params = new URLSearchParams(item.extra || {});
  if (item.scoped && workspaceId) params.set('workspace', workspaceId);
  const query = params.toString();
  return query ? `${item.to}?${query}` : item.to;
}

export function activeItemId(pathname, search = '') {
  for (const group of NAV_GROUPS) {
    const found = group.items.find((item) => item.match(pathname, search));
    if (found) return found.id;
  }
  return FOOTER_ITEMS.find((item) => item.match(pathname, search))?.id || null;
}

// The product switcher: the main app is Auto Appy, this app is AI.
export const PRODUCT_TONES = { main: '#147DB5', whatsapp: '#1F9D57', crm: '#183F5A', ai: '#37A1D2' };
export const isAiProduct = (app) => /(^|[-_\s])ai($|[-_\s])/i.test(String(app?.key || '')) || /\bAI\b/.test(String(app?.name || ''));
export const productTone = (app) => PRODUCT_TONES[app?.key] || (isAiProduct(app) ? PRODUCT_TONES.ai : '#3A5264');
// Every product is called "Auto Appy …", so its mark uses what comes after.
const PRODUCT_MARKS = { main: 'AA', whatsapp: 'WA', crm: 'CRM' };
export function productMark(app) {
  if (PRODUCT_MARKS[app?.key]) return PRODUCT_MARKS[app.key];
  if (isAiProduct(app)) return 'AI';
  const rest = String(app?.name || '').replace(/^auto\s*appy\s*/i, '').trim();
  return (rest.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 2) || 'AA').toUpperCase();
}
